<template>
  <v-card>
    <v-card-title class="d-flex align-center">
      Sons système
      <v-spacer />

      <v-text-field
        v-model="searchQuery"
        clearable
        density="compact"
        hide-details
        label="Rechercher un son..."
        prepend-inner-icon="mdi-magnify"
        style="max-width: 300px;"
        variant="outlined"
      />
    </v-card-title>

    <v-card-text>
      <v-table class="system-sounds-table" density="compact">
        <thead>
          <tr>
            <th
              class="cursor-pointer text-primary"
              style="user-select: none;"
              @click="toggleSort"
            >
              Nom
              <v-icon class="ml-1" size="small">
                {{ sortDirection === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
              </v-icon>
            </th>
            <th>Version</th>
            <th>Description</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>

          <tr class="creation-row">
            <td>
              <v-text-field
                v-model="newSound.name"
                density="compact"
                hide-details
                placeholder="Ex: intro.mp3"
                style="min-width: 150px; max-width: 200px"
                variant="outlined"
              />
            </td>
            <td class="text-medium-emphasis">-</td>
            <td class="text-medium-emphasis">-</td>
            <td>
              <div class="d-flex align-center">
                <v-file-input
                  v-model="newSound.file"
                  accept=".mp3, .opus, audio/mpeg, audio/mp3, audio/opus, audio/ogg"
                  class="mr-3"
                  density="compact"
                  hide-details
                  label=""
                  placeholder="Sélectionnez un fichier"
                  prepend-icon=""
                  prepend-inner-icon="mdi-paperclip"
                  :rules="fileRules"
                  style="min-width: 200px; max-width: 300px"
                  variant="outlined"
                />
                <v-btn
                  color="primary"
                  :disabled="!isNewSoundValid"
                  elevation="0"
                  :loading="uploading"
                  @click="createSound"
                >
                  Créer
                </v-btn>
              </div>
            </td>
          </tr>

          <tr v-for="sound in filteredAndSortedSounds ?? []" :key="sound.name">
            <td class="font-weight-medium">{{ sound.name }}</td>
            <td>{{ sound.version }}</td>
            <td>
              <v-text-field
                v-if="editingSoundName === sound.name"
                v-model="draftDescription"
                autofocus
                density="compact"
                hide-details
                placeholder="Ajouter une description..."
                variant="underlined"
                @keyup.enter="saveDescription(sound)"
                @keyup.esc="cancelEdit"
              />
              <span v-else :class="{'text-grey text-caption': !sound.description}">
                {{ sound.description || 'Aucune description' }}
              </span>

              <template v-if="editingSoundName === sound.name">
                <v-btn
                  color="success"
                  icon="mdi-check"
                  size="small"
                  variant="text"
                  @click="saveDescription(sound)"
                />
                <v-btn
                  color="error"
                  icon="mdi-close"
                  size="small"
                  variant="text"
                  @click="cancelEdit"
                />
              </template>

              <template v-else>
                <v-btn
                  color="primary"
                  icon="mdi-pencil"
                  size="small"
                  variant="text"
                  @click="editDescription(sound)"
                />

              </template>
            </td>
            <td>
              <div class="d-flex align-center">
                <v-btn
                  :aria-label="(playingSoundName === sound.name ? 'Stopper ' : 'Écouter ') + sound.name"
                  class="mr-1"
                  :color="playingSoundName === sound.name ? 'primary' : 'default'"
                  icon
                  :loading="fetchingSoundName === sound.name"
                  size="small"
                  variant="text"
                  @click="togglePlay(sound.name)"
                >
                  <v-icon size="large">
                    {{ playingSoundName === sound.name ? 'mdi-stop-circle' : 'mdi-play-circle' }}
                  </v-icon>
                </v-btn>

                <v-btn
                  class="mr-1"
                  color="info"
                  :disabled="actionLoading !== null"
                  icon
                  :loading="actionLoading === sound.name"
                  size="small"
                  variant="text"
                  @click="triggerReuploadClick(sound.name)"
                >
                  <v-icon size="large">mdi-upload</v-icon>
                  <v-tooltip activator="parent" location="top">Mettre à jour le fichier</v-tooltip>
                </v-btn>

                <v-btn
                  color="error"
                  :disabled="actionLoading !== null"
                  icon
                  :loading="actionLoading === sound.name"
                  size="small"
                  variant="text"
                  @click="deleteSound(sound.name)"
                >
                  <v-icon size="large">mdi-delete</v-icon>
                </v-btn>
              </div>
            </td>
          </tr>

          <tr v-if="filteredAndSortedSounds.length === 0 && !loading">
            <td class="text-center text-medium-emphasis py-4" colspan="3">
              Aucun son trouvé.
            </td>
          </tr>

        </tbody>
      </v-table>
    </v-card-text>

    <input
      ref="hiddenFileInput"
      accept=".mp3, .opus, audio/mpeg, audio/mp3, audio/opus, audio/ogg"
      style="display: none"
      type="file"
      @change="handleHiddenFileChange"
    >
  </v-card>
</template>

<script lang="ts" setup>
  import type { Sound } from '@/model/sound.ts'
  import type { SoundsList } from '@/model/sounds-list.ts'
  import { computed, onMounted, reactive, ref, watch } from 'vue'
  import { systemSoundsRepository } from '@/api/repository/system-sounds.repository.ts'
  import { ToastType, useToastStore } from '@/stores/toast.store.ts'
  import { notifyApiError } from '@/utils/error.utils.ts'

  // --- États pour la recherche et le tri ---
  const searchQuery = ref('')
  const sortDirection = ref<'asc' | 'desc'>('asc')

  const filteredAndSortedSounds = computed(() => {
    let list = availableSounds.value?.sounds ?? []

    // 1. Filter by search query (case-insensitive)
    if (searchQuery.value) {
      const query = searchQuery.value.toLowerCase()

      list = list.filter(sound => {
        const matchName = sound.name.toLowerCase().includes(query)

        // Safely check description if it exists
        const matchDesc = sound.description
          ? sound.description.toLowerCase().includes(query)
          : false

        return matchName || matchDesc
      })
    }

    // 2. Alphabetical sort by name
    return list.toSorted((a, b) => {
      const comparison = a.name.localeCompare(b.name)
      return sortDirection.value === 'asc' ? comparison : -comparison
    })
  })

  function toggleSort () {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  }

  // --- États généraux ---
  const availableSounds = ref<SoundsList | null>(null)
  const loading = ref(true)
  const uploading = ref(false)
  const actionLoading = ref<string | null>(null)

  // --- États Audio ---
  const playingSoundName = ref<string | null>(null)
  const fetchingSoundName = ref<string | null>(null)
  const audio = ref<HTMLAudioElement | null>(null)
  const currentBlobUrl = ref<string | null>(null)

  // --- Formulaires ---
  const newSound = reactive<{ name: string, file: File | File[] | null }>({ name: '', file: null })

  watch(() => newSound.file, file => {
    if (!file) return
    if (newSound.name) return
    const actualFile = Array.isArray(file) ? file[0] : file
    if (!actualFile) return
    newSound.name = actualFile.name
  })

  // --- GESTION DE L'ÉDITION INLINE ---
  // Stocke le nom du son en cours d'édition (null si aucun)
  const editingSoundName = ref<string | null>(null)
  // Stocke la valeur tapée dans le champ avant validation
  const draftDescription = ref<string>('')

  // Références pour l'upload masqué
  const hiddenFileInput = ref<HTMLInputElement | null>(null)
  const targetReuploadSound = ref<string | null>(null)

  // --- Constantes et Validation ---
  const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2 Mo
  const ALLOWED_MIME_TYPES = new Set(['audio/mpeg', 'audio/mp3', 'audio/opus', 'audio/ogg'])

  const fileRules = [
    (value: any) => {
      const file = Array.isArray(value) ? value[0] : value
      if (!file) return true

      const fileName = file.name.toLowerCase()
      const isAllowedExt = fileName.endsWith('.mp3') || fileName.endsWith('.opus')
      const isAllowedMime = ALLOWED_MIME_TYPES.has(file.type)

      if (!isAllowedExt && !isAllowedMime) {
        return 'Format non supporté. Utilisez .mp3 ou .opus.'
      }
      if (file.size > MAX_FILE_SIZE) {
        return 'Le fichier ne doit pas dépasser 2 Mo.'
      }
      return true
    },
  ]

  const isNewSoundValid = computed(() => {
    const file = Array.isArray(newSound.file) ? newSound.file[0] : newSound.file
    if (!newSound.name || !file) return false

    const fileName = file.name.toLowerCase()
    const isAllowedExt = fileName.endsWith('.mp3') || fileName.endsWith('.opus')
    const isAllowedMime = ALLOWED_MIME_TYPES.has(file.type)

    return (isAllowedExt || isAllowedMime) && file.size <= MAX_FILE_SIZE
  })

  // --- Fonctions ---

  async function loadSounds () {
    try {
      availableSounds.value = await systemSoundsRepository.list()
    } catch (error) {
      notifyApiError('Erreur lors du chargement des sons système', error)
    } finally {
      loading.value = false
    }
  }

  async function createSound () {
    if (!isNewSoundValid.value) return

    uploading.value = true
    try {
      const actualFile = Array.isArray(newSound.file) ? newSound.file[0] : newSound.file
      if (!actualFile) throw new Error('Fichier introuvable')

      const formData = new FormData()
      formData.append('file', actualFile, newSound.name)

      await systemSoundsRepository.create(formData)
      useToastStore().notify('Son ajouté', ToastType.Success)

      newSound.name = ''
      newSound.file = null
      await loadSounds()
    } catch (error) {
      notifyApiError('Erreur lors de l\'ajout du son', error)
    } finally {
      uploading.value = false
    }
  }

  async function togglePlay (name: string) {
    if (playingSoundName.value === name) {
      audio.value?.pause()
      playingSoundName.value = null
      return
    }

    if (audio.value) {
      audio.value.pause()
      playingSoundName.value = null
    }

    if (currentBlobUrl.value) {
      URL.revokeObjectURL(currentBlobUrl.value)
    }

    try {
      fetchingSoundName.value = name
      const blobUrl = await systemSoundsRepository.getSoundBlobUrl(name)
      currentBlobUrl.value = blobUrl

      audio.value = new Audio(blobUrl)
      audio.value.addEventListener('ended', () => {
        playingSoundName.value = null
      })

      await audio.value.play()
      playingSoundName.value = name
    } catch (error) {
      notifyApiError('Impossible de lire le son', error)
    } finally {
      fetchingSoundName.value = null
    }
  }

  async function deleteSound (name: string) {
    if (!confirm(`Supprimer définitivement le son "${name}" ?`)) return

    actionLoading.value = name
    try {
      if (playingSoundName.value === name && audio.value) {
        audio.value.pause()
        playingSoundName.value = null
      }

      await systemSoundsRepository.delete(name)
      useToastStore().notify('Son supprimé', ToastType.Success)
      await loadSounds()
    } catch (error) {
      notifyApiError('Erreur lors de la suppression du son', error)
    } finally {
      actionLoading.value = null
    }
  }

  // --- Fonctions pour l'upload masqué ---

  function triggerReuploadClick (name: string) {
    targetReuploadSound.value = name
    if (hiddenFileInput.value) {
      // Réinitialise la valeur pour permettre de re-sélectionner le même fichier si besoin
      hiddenFileInput.value.value = ''
      hiddenFileInput.value.click()
    }
  }

  async function handleHiddenFileChange (event: Event) {
    const target = event.target as HTMLInputElement
    if (!target.files || target.files.length === 0 || !targetReuploadSound.value) return

    const file = target.files[0]

    // NOUVEAU : La vérification stricte qui rassure TypeScript
    if (!file) return

    const name = targetReuploadSound.value

    // Validation manuelle
    const fileName = file.name.toLowerCase()
    const isAllowedExt = fileName.endsWith('.mp3') || fileName.endsWith('.opus')
    const isAllowedMime = ALLOWED_MIME_TYPES.has(file.type)

    if ((!isAllowedExt && !isAllowedMime) || file.size > MAX_FILE_SIZE) {
      useToastStore().notify('Fichier invalide (Format ou taille)', ToastType.Error)
      targetReuploadSound.value = null
      return
    }

    actionLoading.value = name
    try {
      const formData = new FormData()
      formData.append('file', file, name)

      await systemSoundsRepository.create(formData)
      useToastStore().notify('Son mis à jour avec succès', ToastType.Success)
      await loadSounds()
    } catch (error) {
      notifyApiError('Erreur lors de la mise à jour du son', error)
    } finally {
      actionLoading.value = null
      targetReuploadSound.value = null

      if (hiddenFileInput.value) {
        hiddenFileInput.value.value = ''
      }
    }
  }

  function editDescription (sound: Sound) {
    editingSoundName.value = sound.name
    draftDescription.value = sound.description || ''
  }

  function cancelEdit () {
    editingSoundName.value = null
    draftDescription.value = ''
  }

  async function saveDescription (sound: Sound) {
    try {
      await systemSoundsRepository.updateSystemSound(sound.name, { description: draftDescription.value })
      sound.description = draftDescription.value
      editingSoundName.value = null

      useToastStore().notify('Description mise à jour', ToastType.Success)
    } catch (error) {
      notifyApiError('Erreur lors de la mise à jour de la description', error)
    }
  }

  onMounted(() => {
    loading.value = true
    loadSounds()
  })
</script>

<style scoped>
.system-sounds-table th,
.system-sounds-table td {
  padding: 12px 16px !important;
  vertical-align: middle;
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}
</style>
